import { FC, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import styles from "./lessons.module.css";
import {
  Button,
  Input,
  Modal,
  notification,
  Space,
  Table,
} from "antd";
import type { ColumnsType } from "antd/lib/table";
import { SearchOutlined } from "@ant-design/icons";
import { GetLessons_getLessons } from "./../../__generated__/gql-types/GetLessons";
import { useLessons } from "../../hooks/useLessons";
import { formatGrade } from "../../utils/formatGrade";
import useDuplicateLesson from "../../api/useDuplicateLesson";

import { GetCoursesListBackOffice_getCoursesListBackOffice_grade } from "../../__generated__/gql-types/GetCoursesListBackOffice";
import { useMonitoring } from "../../hooks/useMonitoring";
import { USER_AUTH } from "../../store/selectors";
import { useSelector } from "react-redux";

const { Search } = Input;

interface DuplicateModalState {
  visible: boolean;
  lessonId: string;
  lessonTitle: string;
  newTitle: string;
}

const Lessons: FC = () => {
  const { lessons, deleteLesson, loading: isLoading } = useLessons();
  const monitor = useMonitoring();
  const user = useSelector(USER_AUTH);
  const { duplicateLesson, loading: isDuplicating } = useDuplicateLesson();
  const [dataSource, setdataSource] = useState<GetLessons_getLessons[]>([]);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [idLesson, setIdLesson] = useState<string>("");
  const [duplicateModal, setDuplicateModal] = useState<DuplicateModalState>({
    visible: false,
    lessonId: "",
    lessonTitle: "",
    newTitle: "",
  });

  const columns: ColumnsType<GetLessons_getLessons> = [
    {
      title: "id",
      dataIndex: "id",
      key: "id",
    },
    {
      title: "  Título",
      dataIndex: "title",
      key: "title",
    },
    {
      align: "center",
      title: "Grado",
      dataIndex: "grade",
      key: "grade",
      render: (
        grade: GetCoursesListBackOffice_getCoursesListBackOffice_grade
      ) => {
        return <div className={styles.textDraw}>{formatGrade(grade)}</div>;
      },
    },
    {
      title: "Tipo",
      dataIndex: "lesson_type",
      key: "lesson_type",
      render: (lesson_type: string): string => {
        const lessonTypes: Record<string, string> = {
          videolesson: "Video Lección",
          slides: "Diapositivas",
          book_pages: "Páginas del libro",
          classroomguide: "Guión de clase",
          evaluation: "Evaluación",
          redaction: "Redacción",
          literacy: "Lectoescritura",
          listening: "Listening",
          vocabulary: "Vocabulary",
          conversation: "Conversación IA",
          audio_exam: "Examen de Audio",
          writing_exam: "Examen de Escritura",
          manipulative_dynamics: "Dinámicas Manipulativos"
        };
        return lessonTypes[lesson_type] || lesson_type;
      },
    },
    {
      title: "Action",
      key: "action",
      render: (_, record: GetLessons_getLessons) => {
        const records = record;
        return (
          <Space size="middle">
            <Link to={`/lessons/${record.id}`} state={records}>
              <Button icon={<SearchOutlined />}>Editar</Button>
            </Link>
            <Button
              type="primary"
              onClick={() => {
                setDuplicateModal({
                  visible: true,
                  lessonId: record.id,
                  lessonTitle: record.title || "",
                  newTitle: `${record.title || ""} (copia)`,
                });
              }}
            >
              Duplicar
            </Button>
            <Button
              onClick={() => {
                showModal(record.id);
              }}
              danger
            >
              Eliminar
            </Button>
          </Space>
        );
      },
    },
  ];

  const showModal = (id: string) => {
    setIsModalVisible(true);
    setIdLesson(id);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  useEffect(() => {
    setdataSource(lessons);
  }, [lessons]);

  const handleOk = async () => {
    try {
      monitor.captureMessage("Delete lesson", "info", { idLesson, userEmail: user?.email });
      await deleteLesson(idLesson);
      setIsModalVisible(false);
    } catch {
      notification["error"]({
        message: "Eliminación Erronea",
        description: "La leccion no pudo ser eliminada!",
        placement: "top",
        duration: 3,
      });
    }
  };

  const onSearch = (value: string) => {
    if (value === "") {
      setdataSource(lessons);
    } else {
      const filteredData = dataSource.filter(
        (data) => data.title?.toLowerCase().includes(value.toLowerCase())
      );
      setdataSource(filteredData);
    }
  };

  const onSearchId = (value: string) => {
    if (value === "") {
      setdataSource(lessons);
    } else {
      const filteredData = dataSource.filter(
        (data) => data.id === value
      );
      setdataSource(filteredData);
    }
  };
  return (
    <div className={styles.container}>
      <h1>Lecciones</h1>
      <div className={styles.lessonsContent}>
        <Space direction="vertical" style={{ marginRight: 20 }}>
          <Search
            placeholder="Buscar por id"
            onSearch={onSearchId}
            enterButton
          />
        </Space>
        <Space direction="vertical" style={{ marginRight: 20 }}>
          <Search
            placeholder="Buscar por título"
            onSearch={onSearch}
            enterButton
          />
        </Space>
        <Link to="/lessons/new">
          <Button type="primary" danger>
            Nueva Lección
          </Button>
        </Link>
      </div>
      <Table
        columns={columns}
        dataSource={dataSource ?? []}
        loading={isLoading}
        sortDirections={["ascend"]}
        rowKey={(record) => record.id}
      />
      <Modal
        title="Eliminar Programa"
        visible={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
      >
        <p>¿Desea eliminar el programa?</p>
      </Modal>

      <Modal
        title="Duplicar Lección"
        visible={duplicateModal.visible}
        onOk={async () => {
          if (!duplicateModal.newTitle.trim()) {
            notification["error"]({
              message: "Error",
              description: "El título no puede estar vacío",
              placement: "top",
              duration: 3,
            });
            return;
          }

          try {
            const result = await duplicateLesson({
              id: duplicateModal.lessonId,
              newTitle: duplicateModal.newTitle
            });

            if (result) {
              // Actualizar el dataSource manualmente
              // No actualizamos manualmente el dataSource ya que refetchQueries se encargará
              
              notification["success"]({
              message: "Lección Duplicada",
              description: "La lección ha sido duplicada exitosamente!",
              placement: "top",
              duration: 3,
            });
              setDuplicateModal(prev => ({ ...prev, visible: false }));
            } else {
            }
          } catch (error) {

            notification["error"]({
              message: "Error al Duplicar",
              description: error instanceof Error ? error.message : "La lección no pudo ser duplicada!",
              placement: "top",
              duration: 3,
            });
          }
        }}
        onCancel={() => setDuplicateModal(prev => ({ ...prev, visible: false }))}
        okButtonProps={{ loading: isDuplicating }}
      >
        <div style={{ marginBottom: 16 }}>
          <p>Ingrese el nombre para la lección duplicada:</p>
          <Input
            value={duplicateModal.newTitle}
            onChange={(e) => setDuplicateModal(prev => ({ ...prev, newTitle: e.target.value }))}
            placeholder="Nombre de la lección"
          />
        </div>
      </Modal>
    </div>
  );
};

export default Lessons;
